<script setup lang="ts">
import type { Property } from '@/core/entities/property';
import type { ShortVideo } from '~/composables/shorts';
import type { ListingTrackingContext } from '~/composables/useListingEventTracking';
import type {
	PropertyFilters,
	ShortsFilters
} from '~/core/entities/property/filters';
import type { SectionName } from '~/types/mixpanel';
import type { H3ClusterId } from '~~/core/ports/repositories';
import type { ComponentPublicInstance } from 'vue';

import { cn, hasShortsVideo } from '@/utils';
import {
	breakpointsTailwind,
	useBreakpoints,
	useIntersectionObserver,
	useMounted
} from '@vueuse/core';
import {
	mapPropertyToShortVideo,
	toShortVideoProperty
} from '~/composables/shorts/mappers';
import { SHORTS_DEFAULT_PER_PAGE } from '~/composables/shorts/useFetchShorts';
import { useOpenShortsFeed } from '~/composables/shorts/useOpenShortsFeed';
import { BOTTOM_SHEET_DRAG_ZONE_ATTR } from '~/composables/useBottomSheet';
import { hasCardPreloadSignal } from '~/composables/useCardPreloadBudget';
import { useCarouselPreloadWindow } from '~/composables/useCarouselPreloadWindow';
import { withFeedBaseFilters } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { warmVideoManifests } from '~/composables/useVideoWarmup';
import { ORDER_BY, SHORTS_SORT_BY } from '~/core/entities/property/filters';
import { EventBusKeys } from '~/core/helpers/symbols';

import { VideoCard } from '~/components/VideoCard';

import PropertyListShortsSection from '../PropertyList/PropertyListShortsSection.vue';

const props = withDefaults(
	defineProps<{
		properties: Property[];
		showCardActions?: boolean;
		section?: SectionName;
		shouldShowShortsSection?: boolean;
		shortsDisplayMode?: 'first-fold' | 'every-six';
		useShortsCardsOnMobile?: boolean;
		shortsInfoLabel?: string | null;
		/** Filtros da sessão da listagem para shorts (cidade/UF após `locationSlugFilters`). */
		listingSessionFilters?: PropertyFilters;
		trackingContext?: ListingTrackingContext;
		favoriteButtonClass?: string;
		isDragging?: boolean;
	}>(),
	{
		showCardActions: false,
		section: 'listed properties',
		shouldShowShortsSection: false,
		shortsDisplayMode: 'first-fold',
		useShortsCardsOnMobile: false,
		shortsInfoLabel: null
	}
);

const shouldRenderShortsSection = computed(
	() => props.shouldShowShortsSection && props.properties.length > 0
);

const SHORTS_VARIANT_GROUP_SIZE = 6;
// includeImagesEvery injects one image item in each group, so per_page needs one extra slot.
const SHORTS_VARIANT_FETCH_PER_PAGE = SHORTS_VARIANT_GROUP_SIZE + 1;
const SHORTS_VARIANT_MAX_VIDEOS = SHORTS_VARIANT_GROUP_SIZE;
const SHORTS_VARIANT_TITLE = 'Amplie sua busca em vídeos.';
const SHORTS_VARIANT_VISIBILITY_ROOT_MARGIN = '900px 0px';

const shouldRenderFirstFoldShortsSection = computed(
	() =>
		shouldRenderShortsSection.value && props.shortsDisplayMode === 'first-fold'
);

const everySixShortsSections = computed(() => {
	if (
		!shouldRenderShortsSection.value ||
		props.shortsDisplayMode !== 'every-six'
	) {
		return [];
	}

	return Array.from(
		{ length: Math.floor(props.properties.length / SHORTS_VARIANT_GROUP_SIZE) },
		(_, index) => ({
			key: `shorts-section-${index}`,
			fetchPage: index + 1,
			shouldTrackPageview: index === 0,
			order: getEverySixShortsSectionOrder(index)
		})
	);
});

function getPropertyOrder(index: number): number {
	return (index + 1) * 10;
}

function getEverySixShortsSectionOrder(sectionIndex: number): number {
	const afterPropertyCount = (sectionIndex + 1) * SHORTS_VARIANT_GROUP_SIZE;
	return afterPropertyCount * 10 + 5;
}

const defaultPropertyCardClasses = 'cursor-pointer h-[340px] md:h-[378px]';
const shortsSrpImageCardClasses =
	'cursor-pointer h-[340px] sm:h-[488px] lg:h-[378px]';
const shortsSrpVideoCardClasses =
	'cursor-pointer !h-[488px] !w-full sm:!h-auto sm:!w-[335px] sm:max-w-full sm:min-h-[488px] sm:justify-self-center sm:self-stretch lg:!h-[378px] lg:min-h-0 lg:!w-full lg:justify-self-stretch';
const shortsSrpPlayerRootMargin = '150px';
const SHORTS_AUTOPLAY_MIN_VISIBLE_RATIO = 0.3;
const SHORTS_AUTOPLAY_THRESHOLDS = Array.from(
	{ length: 11 },
	(_, index) => index / 10
);

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobileSrpViewport = breakpoints.smaller('lg');
// SSR has no viewport, so `smaller('lg')` is false server-side and the server
// always renders PropertyCard. Gating the VideoCard swap on `isMounted` keeps
// the first client (hydration) render identical to the server, then swaps to
// VideoCard post-hydration — avoiding a Vue hydration mismatch on mobile.
const isMounted = useMounted();

type ShortsCardTemplateRef = Element | ComponentPublicInstance;
type ShortsCardObserverTarget =
	| HTMLElement
	| SVGElement
	| ComponentPublicInstance;

// Keep the target array unchanged across ordinary renders so VueUse does not
// disconnect and recreate the IntersectionObserver unless a card actually changes.
const observedShortsCardRefs = shallowRef<ShortsCardObserverTarget[]>([]);
const shortsCardRefsByPropertyId = new Map<
	Property['id'],
	ShortsCardObserverTarget
>();
const shortsCardRefCallbacks = new Map<
	Property['id'],
	(templateRef: ShortsCardTemplateRef | null) => void
>();
const shortsCardVisibleRatios = new Map<Property['id'], number>();
const activeShortsCardPropertyId = ref<Property['id'] | null>(null);

function setShortsCardRef(
	propertyId: Property['id'],
	templateRef: ShortsCardTemplateRef | null
): void {
	if (shortsCardRefsByPropertyId.get(propertyId) === templateRef) return;

	if (templateRef) {
		shortsCardRefsByPropertyId.set(
			propertyId,
			templateRef as ShortsCardObserverTarget
		);
	} else {
		shortsCardRefsByPropertyId.delete(propertyId);
		shortsCardRefCallbacks.delete(propertyId);
	}

	observedShortsCardRefs.value = [...shortsCardRefsByPropertyId.values()];
}

function getShortsCardRef(
	propertyId: Property['id']
): (templateRef: ShortsCardTemplateRef | null) => void {
	const currentCallback = shortsCardRefCallbacks.get(propertyId);
	if (currentCallback) return currentCallback;

	const callback = (templateRef: ShortsCardTemplateRef | null) =>
		setShortsCardRef(propertyId, templateRef);
	shortsCardRefCallbacks.set(propertyId, callback);

	return callback;
}

function updateActiveShortsCard(): void {
	let nextActivePropertyId: Property['id'] | null = null;
	let highestVisibleRatio = SHORTS_AUTOPLAY_MIN_VISIBLE_RATIO;

	for (const [propertyId, visibleRatio] of shortsCardVisibleRatios) {
		if (visibleRatio < highestVisibleRatio) continue;

		nextActivePropertyId = propertyId;
		highestVisibleRatio = visibleRatio;
	}

	activeShortsCardPropertyId.value = nextActivePropertyId;
}

useIntersectionObserver(
	observedShortsCardRefs,
	entries => {
		for (const entry of entries) {
			const propertyId = (entry.target as HTMLElement).dataset
				.shortsAutoplayPropertyId;
			if (!propertyId) continue;

			if (!entry.isIntersecting) {
				shortsCardVisibleRatios.delete(propertyId);
				continue;
			}

			shortsCardVisibleRatios.set(propertyId, entry.intersectionRatio);
		}

		updateActiveShortsCard();
	},
	{ threshold: SHORTS_AUTOPLAY_THRESHOLDS }
);

const { openShortsFeed } = useOpenShortsFeed();

const shortsFeedFilters = computed<ShortsFilters>(() => {
	const listingFilters = props.listingSessionFilters ?? {};

	return {
		regions: listingFilters.regions,
		cities: listingFilters.cities,
		states: listingFilters.states,
		macroregions: listingFilters.macroregions,
		propertyTypes: listingFilters.propertyTypes,
		minAskingPrice: listingFilters.minAskingPrice,
		maxAskingPrice: listingFilters.maxAskingPrice,
		excludeProperties: listingFilters.excludeProperties,
		poiLocationLat: listingFilters.poiLocationLat,
		poiLocationLng: listingFilters.poiLocationLng,
		sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
		orderBy: ORDER_BY.DESC
	};
});

const shortsCardsByPropertyId = computed(() => {
	const cards = new Map<Property['id'], ShortVideo>();

	if (
		!props.useShortsCardsOnMobile ||
		!isMounted.value ||
		!isMobileSrpViewport.value
	)
		return cards;

	for (const property of props.properties) {
		if (!hasShortsVideo(property)) continue;

		const card = mapPropertyToShortVideo(toShortVideoProperty(property));
		if (card?.kind !== 'video') continue;

		cards.set(property.id, card);
	}

	return cards;
});

watch(shortsCardsByPropertyId, currentShortsCards => {
	for (const propertyId of shortsCardVisibleRatios.keys()) {
		if (!currentShortsCards.has(propertyId)) {
			shortsCardVisibleRatios.delete(propertyId);
		}
	}

	updateActiveShortsCard();

	// Warm the first shorts manifests (~400 B each, idle-time, Data Saver and
	// dedupe guards inside) so an autoplaying card skips the manifest RTT.
	warmVideoManifests(
		[...currentShortsCards.values()].map(card => card.videoUrl)
	);
});

function getShortsCard(property: Property): ShortVideo | null {
	return shortsCardsByPropertyId.value.get(property.id) ?? null;
}

const propertyListItems = computed(() =>
	props.properties.map(property => ({
		property,
		shortsCard: getShortsCard(property)
	}))
);

// A lista rola em uma coluna, entao pagina igual a um carrossel vertical: o
// "indice atual" e o card que o observer de autoplay elegeu (>=30% visivel).
const activeShortsCardIndex = computed(() => {
	const activeId = activeShortsCardPropertyId.value;
	if (activeId === null) return null;
	const at = propertyListItems.value.findIndex(
		item => item.property.id === activeId
	);
	return at === -1 ? null : at;
});

const { preloadDistanceFor } = useCarouselPreloadWindow(
	() => activeShortsCardIndex.value
);

function getShortsCardsFrom(index: number): ShortVideo[] {
	return propertyListItems.value
		.slice(index)
		.map(({ shortsCard }) => shortsCard)
		.filter((card): card is ShortVideo => card !== null);
}

function getPropertyCardRootClasses(property: Property, index: number): string {
	return cn({
		'mb-[30px]': index !== 0 && property.poiDistanceTime
	});
}

function getPropertyCardClasses(): string {
	return props.useShortsCardsOnMobile
		? shortsSrpImageCardClasses
		: defaultPropertyCardClasses;
}

function getShortsCardClasses(property: Property, index: number): string {
	return cn(shortsSrpVideoCardClasses, 'lg:hidden', {
		'mb-[30px]': index !== 0 && property.poiDistanceTime
	});
}

function handleShortsCardClick(property: Property, index: number): void {
	const filters = withFeedBaseFilters(shortsFeedFilters.value);

	openShortsFeed({
		at: property.commercialId,
		page: {
			items: getShortsCardsFrom(index),
			cursor: {
				filters,
				page: 1,
				perPage: SHORTS_DEFAULT_PER_PAGE,
				hasMore: true
			},
			meta: {}
		}
	});
}

const propertyCardHover = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardHover
);
const propertyCardUnhover = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardUnhover
);

function handlePropertyCardMouseEnter(property: Property) {
	if (!property.h3ClusterIds?.length) return;

	propertyCardHover.emit(property.h3ClusterIds);
}

function handlePropertyCardMouseLeave(property: Property) {
	if (!property.h3ClusterIds?.length) return;

	propertyCardUnhover.emit(property.h3ClusterIds);
}
</script>

<template>
	<template v-for="(item, index) in propertyListItems" :key="item.property.id">
		<VideoCard
			v-if="item.shortsCard"
			:ref="getShortsCardRef(item.property.id)"
			:[BOTTOM_SHEET_DRAG_ZONE_ATTR]="index === 0 ? '' : undefined"
			:data-shorts-autoplay-property-id="item.property.id"
			:style="{ order: getPropertyOrder(index) }"
			:class="getShortsCardClasses(item.property, index)"
			:property="item.shortsCard.property"
			:thumbnail-url="item.shortsCard.thumbnailUrl"
			:video-url="item.shortsCard.videoUrl"
			:index="index"
			:card-position="index"
			:section-name="section"
			:is-active="activeShortsCardPropertyId === item.property.id"
			:hover-to-play="false"
			:preload-when-visible="hasCardPreloadSignal"
			:prefetch-variants="item.shortsCard.prefetchVariants"
			:preload-distance="preloadDistanceFor(index)"
			:player-root-margin="shortsSrpPlayerRootMargin"
			@click="handleShortsCardClick(item.property, index)"
			@mouseenter="handlePropertyCardMouseEnter(item.property)"
			@mouseleave="handlePropertyCardMouseLeave(item.property)"
		/>

		<PropertyCard
			v-else
			variant="whiteBackground"
			:header-size="
				props.useShortsCardsOnMobile ? 'shortsSrpMobile' : undefined
			"
			:[BOTTOM_SHEET_DRAG_ZONE_ATTR]="index === 0 ? '' : undefined"
			:style="{ order: getPropertyOrder(index) }"
			:root-class="getPropertyCardRootClasses(item.property, index)"
			:class="getPropertyCardClasses()"
			:property="item.property"
			:section="section"
			:show-actions="showCardActions"
			:card-position="index"
			:tracking-context="trackingContext"
			:position-in-list="index"
			:favorite-button-class="props.favoriteButtonClass"
			:is-dragging="props.isDragging"
			:as-tags="{
				addresses: 'h3',
				askingPrice: 'h4',
				infoItems: 'h5'
			}"
			@mouseenter="handlePropertyCardMouseEnter(item.property)"
			@mouseleave="handlePropertyCardMouseLeave(item.property)"
		/>
	</template>

	<PropertyListShortsSection
		v-if="shouldRenderFirstFoldShortsSection"
		class="col-span-full order-[25] md:order-[45]"
		:info-label="props.shortsInfoLabel"
		:session-filters="props.listingSessionFilters"
	/>

	<PropertyListShortsSection
		v-for="shortsSection in everySixShortsSections"
		:key="shortsSection.key"
		class="col-span-full"
		:style="{ order: shortsSection.order }"
		:info-label="props.shortsInfoLabel"
		:session-filters="props.listingSessionFilters"
		:title-override="SHORTS_VARIANT_TITLE"
		:skip-pageview-tracking="!shortsSection.shouldTrackPageview"
		:fetch-page="shortsSection.fetchPage"
		:fetch-per-page="SHORTS_VARIANT_FETCH_PER_PAGE"
		:max-videos="SHORTS_VARIANT_MAX_VIDEOS"
		defer-until-visible
		:visibility-root-margin="SHORTS_VARIANT_VISIBILITY_ROOT_MARGIN"
	/>
</template>
